import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useLayoutEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import getClassNameFactory from "../../utilities/classNames";
import Dropdown from "../../modules/Dropdown";
import BreadcrumbItem from "./components/BreadcrumbItem";
import { Breakpoints } from "../../core";
import uniqueId from "lodash/uniqueId";

var Breadcrumb = function Breadcrumb(_ref) {
  var children = _ref.children,
      buttonAriaLabel = _ref.buttonAriaLabel,
      breadcrumbId = _ref.breadcrumbId;
  var getClassName = getClassNameFactory(Breadcrumb.displayName);
  var jsonLdItems = [];
  var cloneChildren = [];

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      overflown = _useState2[0],
      setOverflown = _useState2[1];

  var _useState3 = useState(2),
      _useState4 = _slicedToArray(_useState3, 2),
      maxEndItems = _useState4[0],
      setMaxEndItems = _useState4[1];

  var _useState5 = useState(1),
      _useState6 = _slicedToArray(_useState5, 2),
      maxStartItems = _useState6[0],
      setMaxStartItems = _useState6[1];

  var divRef = useRef(null);
  var cloneRef = useRef(null);
  var dropdownItems = [];
  var dropdownMenuId = uniqueId("dropdown-menu-id-");
  var breadcrumbsIdRef = useRef(uniqueId("breadcumbs-id-"));
  React.Children.forEach(children, function (child, i) {
    if (child.type === BreadcrumbItem) {
      var position = i + 1;
      jsonLdItems.push({
        "@type": "ListItem",
        position: position,
        item: {
          "@id": child.props.href,
          name: child.props.text
        }
      });
      cloneChildren.push(child);
    }
  });

  var isOverflown = function isOverflown() {
    var isoverflown = false;

    if (divRef.current && cloneRef.current) {
      cloneRef.current.setAttribute("style", "display:flex");
      isoverflown = divRef.current.clientWidth < Breakpoints.DESKTOP || divRef.current.scrollWidth > divRef.current.clientWidth;
      cloneRef.current.setAttribute("style", "");
    }

    return isoverflown;
  };

  var handleWindowResize = debounce(function () {
    var needsWrap = isOverflown();
    setOverflown(needsWrap);

    if (divRef.current && needsWrap) {
      setMaxStartItems(0);
      setMaxEndItems(1);
    } else {
      setMaxStartItems(1);
      setMaxEndItems(3);
    }
  }, 100);
  useLayoutEffect(function () {
    if (window) {
      handleWindowResize();
      window.addEventListener("resize", handleWindowResize);
    } // adds scroll width variable for calculating breadcrumb select width


    document.documentElement.style.setProperty("--scrollbar-width", "".concat(window.innerWidth - document.documentElement.clientWidth, "px"));
    return function () {
      if (window) {
        window.removeEventListener("resize", handleWindowResize);
      }
    };
  }, []);

  if (overflown) {
    var totalItems = cloneChildren.length;
    var centerItems = cloneChildren.splice(maxStartItems, totalItems - maxEndItems);
    centerItems.forEach(function (child) {
      dropdownItems.push({
        href: child.props.href,
        label: child.props.text
      });
    });
    cloneChildren.splice(maxStartItems, 0, /*#__PURE__*/React.createElement("li", {
      className: getClassName({
        descendantName: "dropdown"
      }),
      key: "dropdown"
    }, /*#__PURE__*/React.createElement(Dropdown, {
      block: false,
      items: dropdownItems,
      placeholder: "",
      onValueChanged: function onValueChanged() {},
      dropdownMenuId: dropdownMenuId,
      removeAutoFocus: false // eslint-disable-next-line max-params
      ,
      renderHeader: function renderHeader(title, expanded, toggleExpand, epxandButtonId) {
        return /*#__PURE__*/React.createElement("button", {
          id: epxandButtonId,
          className: getClassName({
            descendantName: "dropdown-btn"
          }),
          onClick: toggleExpand,
          "aria-expanded": expanded,
          "aria-haspopup": true,
          "aria-controls": dropdownMenuId,
          "aria-label": buttonAriaLabel
        }, /*#__PURE__*/React.createElement("svg", {
          width: "16",
          height: "16",
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 448 512"
        }, /*#__PURE__*/React.createElement("path", {
          d: "M8 256a56 56 0 1 1 112 0A56 56 0 1 1 8 256zm160 0a56 56 0 1 1 112 0 56 56 0 1 1 -112 0zm216-56a56 56 0 1 1 0 112 56 56 0 1 1 0-112z"
        })));
      }
    })));
  }

  return /*#__PURE__*/React.createElement("nav", {
    id: breadcrumbId || breadcrumbsIdRef.current,
    className: getClassName(),
    "aria-label": "breadcrumbs",
    ref: divRef
  }, /*#__PURE__*/React.createElement("ol", {
    ref: cloneRef,
    className: getClassName({
      descendantName: "items",
      modifiers: "hidden"
    })
  }, children), /*#__PURE__*/React.createElement("ol", {
    className: getClassName({
      descendantName: "items"
    })
  }, cloneChildren), /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify({
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: jsonLdItems
  })));
};

Breadcrumb.defaultProps = {
  buttonAriaLabel: "breadcrumbs"
};
Breadcrumb.displayName = "Breadcrumb";
Breadcrumb.Item = BreadcrumbItem;
export default Breadcrumb;